// Cliente de la API. Un método por endpoint, tipado, y un solo lugar donde se
// decide qué hacer con los errores.
//
// Rutas relativas a propósito: en producción el mismo Worker sirve la web y la
// API, y en desarrollo el proxy de Vite las manda a wrangler dev.

import type { Tema } from '../nucleo/temas';
import type { DatosCategoria, DatosTorneo, EstadoTorneo, Formato } from '../nucleo/validar';
import type { RenglonMalo } from '../nucleo/parejas';
import type { GrupoGuardado } from '../nucleo/vistaGrupos';
import type { Marcador } from '../nucleo/marcador';
import type { CuadroGuardado } from '../nucleo/vistaCuadro';
import type { Horario } from '../nucleo/programacion';
import type { Configuracion, Habilitacion } from '../nucleo/habilitacion';
import type { ConfiguracionTorneo, capacidades, OpcionesTorneo, Disponibilidad, Solicitud, ImagenTorneo, LugarImagen, Restriccion } from '../nucleo/opciones';

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`/api${ruta}`, {
      ...opciones,
      headers: { 'content-type': 'application/json', ...opciones.headers },
      credentials: 'same-origin',
    });
  } catch {
    throw new ErrorApi(0, 'No hay conexión. Revisá la señal y volvé a intentar.');
  }

  const cuerpo = (await respuesta.json().catch(() => null)) as { error?: string } | T | null;

  if (!respuesta.ok) {
    const mensaje =
      (cuerpo && typeof cuerpo === 'object' && 'error' in cuerpo && cuerpo.error) || `Algo falló (${respuesta.status})`;
    throw new ErrorApi(respuesta.status, String(mensaje));
  }
  return cuerpo as T;
}

const postear = <T>(ruta: string, datos?: unknown) =>
  pedir<T>(ruta, { method: 'POST', body: datos === undefined ? undefined : JSON.stringify(datos) });

// ── Tipos ─────────────────────────────────────────────────────────────────

export interface Pareja {
  id: number;
  jugador1: string;
  jugador2: string;
  cabeza_serie: number | null;
  /** En la página pública, una baja solo viene si estaba en un grupo (abandonó). */
  estado: 'inscripta' | 'baja';
}

export interface ParejaAdmin extends Pareja {
  orden: number;
}

export interface Categoria<P = Pareja> {
  id: number;
  nombre: string;
  formato: Formato;
  tam_grupo: number | null;
  pasan_por_grupo: number | null;
  tema: Tema | null;
  /** Cuántas parejas entran. Solo viene en el panel del organizador: el público no lo recibe. */
  cupo?: number | null;
  parejas: P[];
  grupos: GrupoGuardado[];
  /** null = el cuadro todavía no se armó. */
  cuadro: CuadroGuardado | null;
  /** Qué se lleva cada puesto (1 campeona, 2 subcampeona, 3 semifinalistas). Solo los que tienen algo escrito. */
  premios: { puesto: number; descripcion: string }[];
}

export interface TorneoResumen {
  slug: string;
  nombre: string;
  sede: string | null;
  fecha_desde: string;
  fecha_hasta: string;
  tema: Tema;
  estado: EstadoTorneo;
  canchas: number | null;
}

export interface TorneoAdmin extends TorneoResumen {
  control_canchas_activo?: number;
  id: number;
}

export interface TorneoPublico {
  capacidades: ReturnType<typeof capacidades>;
  inscripcion_publica: boolean;
  imagenes: ImagenTorneo[];
  /** `actualizar_cada_segundos`: lo decide el servidor (más espaciado en el torneo de prueba). */
  torneo: TorneoResumen & { actualizar_cada_segundos: number };
  categorias: Categoria[];
}

export interface TorneoCompleto {
  opciones: ConfiguracionTorneo;
  solicitudes: Solicitud[];
  disponibilidad: Disponibilidad[];
  imagenes: ImagenTorneo[];
  cobro: Cobro;
  torneo: TorneoAdmin;
  categorias: (Categoria<ParejaAdmin> & { torneo_id: number; orden: number })[];
}

export interface Yo {
  organizador: { id: number; nombre: string };
  organizacion: string;
  debe_cambiar_pin: boolean;
  /** El dueño del sitio: ve el panel "Sitio" (clubes, habilitaciones, respaldo). */
  es_dueno: boolean;
}

/** Lo que hace falta para explicar si un torneo se puede publicar (etapa 7). */
export interface Cobro {
  habilitacion: Habilitacion | null;
  /** Al club le queda su torneo de prueba gratis. */
  prueba_disponible: boolean;
  prueba_tope_parejas: number;
  texto_de_pago: string;
  /** Cada cuántos minutos se actualiza sola la página pública: habilitado / de prueba. */
  minutos_pago: number;
  minutos_prueba: number;
  /** Parejas inscriptas en todo el torneo. */
  parejas: number;
}

export interface TorneoDelSitio {
  id: number;
  slug: string;
  nombre: string;
  fecha_desde: string;
  fecha_hasta: string;
  estado: EstadoTorneo;
  parejas: number;
  habilitacion: (Habilitacion & { nota: string | null }) | null;
}

export interface ResumenDelSitio {
  clubes: {
    id: number;
    nombre: string;
    prueba_habilitada: boolean;
    prueba_tope_parejas: number | null;
    prueba_usada: boolean;
    organizadores: { id: number; nombre: string; activo: number; pin_provisorio: number; es_dueno: number }[];
    torneos: TorneoDelSitio[];
  }[];
  configuracion: Configuracion;
}

export interface Organizador {
  id: number;
  nombre: string;
  activo: number;
  pin_provisorio: number;
}

export interface EstadoRespaldo {
  /** Segundos unix del último respaldo, o null si nunca hubo. */
  ultimo: number | null;
  clave: string | null;
  bytes: number | null;
  configurado: boolean;
  /** Días desde el último; null si nunca hubo. */
  dias: number | null;
  /** Pasó más de una semana sin respaldo: hay que avisar. */
  atrasado: boolean;
}

export interface ResultadoCarga {
  guardadas: number;
  parejas: { linea: number; jugador1: string; jugador2: string; cabeza_serie: number | null }[];
  errores: RenglonMalo[];
}

// ── Endpoints ─────────────────────────────────────────────────────────────

export const api = {
  opcionesTorneo: (id: number, d: {cambios: Partial<OpcionesTorneo>; revisiones: Record<string, number>}) => postear<{ok: true}>(`/admin/torneos/${id}/opciones`, d),
  solicitarInscripcion: (slug: string, d: { categoria_id: number; jugador1: string; jugador2: string; contacto: string; sitio: string }) => postear<{ok: true}>(`/publico/t/${slug}/inscripcion`, d),
  resolverSolicitud: (id: number, accion: 'aprobar' | 'rechazar') => postear<{ok: true}>(`/admin/solicitudes/${id}`, {accion}),
  disponibilidadPareja: (id: number, restricciones: Restriccion[]) => postear<{ok: true}>(`/admin/parejas/${id}/disponibilidad`, {restricciones}),
  imagenTorneo: (id: number, lugar: LugarImagen, imagen: string | null) => postear<{ok: true}>(`/admin/torneos/${id}/imagenes/${lugar}`, {imagen}),
  // Público
  torneosPublicos: () => pedir<{ torneos: TorneoResumen[] }>('/publico/torneos'),
  torneoPublico: (slug: string) => pedir<TorneoPublico>(`/publico/t/${encodeURIComponent(slug)}`),

  // Puesta en marcha y sesión
  estadoSetup: () => pedir<{ disponible: boolean }>('/setup'),
  setup: (datos: { clave_alta: string; organizacion: string; nombre: string; pin: string }) =>
    postear<{ ok: true }>('/setup', datos),
  entrar: (nombre: string, pin: string) =>
    postear<{ organizador: { id: number; nombre: string }; debe_cambiar_pin: boolean }>('/auth/entrar', { nombre, pin }),
  salir: () => postear<{ ok: true }>('/auth/salir'),
  yo: () => pedir<Yo>('/auth/yo'),
  cambiarPin: (actual: string, nuevo: string) => postear<{ ok: true }>('/auth/cambiar-pin', { actual, nuevo }),

  // Organizadores
  organizadores: () => pedir<{ organizadores: Organizador[] }>('/admin/organizadores'),
  altaOrganizador: (nombre: string) =>
    postear<{ organizador: { id: number; nombre: string }; pin_provisorio: string }>('/admin/organizadores', { nombre }),
  resetearPin: (id: number) => postear<{ pin_provisorio: string }>(`/admin/organizadores/${id}/resetear-pin`),
  activarOrganizador: (id: number, activo: boolean) =>
    postear<{ ok: true }>(`/admin/organizadores/${id}/activo`, { activo }),

  // El dueño del sitio
  resumenDelSitio: () => pedir<ResumenDelSitio>('/dueno/resumen'),
  invitarOrganizador: (clubId: number, organizadorId: number) =>
    postear<{ pin_provisorio: string }>(`/dueno/clubes/${clubId}/organizadores/${organizadorId}/invitacion`),
  pruebaDelClub: (clubId: number, habilitada: boolean, tope_parejas?: number) =>
    postear<{ ok: true }>(`/dueno/clubes/${clubId}/prueba`, { habilitada, tope_parejas }),
  crearClub: (club: string, organizador: string) =>
    postear<{ club: { id: number; nombre: string }; organizador: { id: number; nombre: string }; pin_provisorio: string }>('/dueno/clubes', { club, organizador }),
  habilitarTorneo: (torneoId: number, h: { tipo: 'pago' | 'regalo'; tope_parejas: number | null; nota: string | null }) =>
    postear<{ ok: true }>(`/dueno/torneos/${torneoId}/habilitar`, h),
  configurarSitio: (c: Configuracion) => postear<{ ok: true }>('/dueno/configuracion', c),

  // Respaldo (solo el dueño)
  respaldo: () => pedir<EstadoRespaldo>('/admin/respaldo'),
  respaldarAhora: () => postear<EstadoRespaldo>('/admin/respaldo'),

  // Torneos
  torneos: () => pedir<{ torneos: TorneoAdmin[] }>('/admin/torneos'),
  crearTorneo: (datos: DatosTorneo) => postear<{ torneo: { id: number; slug: string } }>('/admin/torneos', datos),
  torneo: (id: number) => pedir<TorneoCompleto>(`/admin/torneos/${id}`),
  editarTorneo: (id: number, datos: DatosTorneo) => postear<{ ok: true }>(`/admin/torneos/${id}`, datos),
  estadoTorneo: (id: number, estado: EstadoTorneo) => postear<{ ok: true }>(`/admin/torneos/${id}/estado`, { estado }),

  // Categorías y parejas
  crearCategoria: (torneoId: number, datos: DatosCategoria) =>
    postear<{ categoria: { id: number } }>(`/admin/torneos/${torneoId}/categorias`, datos),
  editarCategoria: (id: number, datos: DatosCategoria) => postear<{ ok: true }>(`/admin/categorias/${id}`, datos),
  cargarParejas: (categoriaId: number, texto: string, probar: boolean) =>
    postear<ResultadoCarga>(`/admin/categorias/${categoriaId}/parejas`, { texto, probar }),
  editarPareja: (id: number, datos: { jugador1: string; jugador2: string; cabeza_serie: number | null }) =>
    postear<{ ok: true }>(`/admin/parejas/${id}`, datos),
  estadoPareja: (id: number, estado: 'inscripta' | 'baja') => postear<{ ok: true }>(`/admin/parejas/${id}/estado`, { estado }),

  // Grupos y resultados
  /** `aMano`: las parejas que el organizador ubicó él (queda en el registro, no cambia el reparto). */
  armarGrupos: (categoriaId: number, grupos: number[][], aMano: number[] = []) =>
    postear<{ ok: true }>(`/admin/categorias/${categoriaId}/grupos`, { grupos, a_mano: aMano }),
  deshacerGrupos: (categoriaId: number) => postear<{ ok: true }>(`/admin/categorias/${categoriaId}/deshacer-grupos`),
  cargarResultado: (partidoId: number, marcador: Marcador) =>
    postear<{ ok: true }>(`/admin/partidos/${partidoId}/resultado`, marcador),
  anularResultado: (partidoId: number) => postear<{ ok: true }>(`/admin/partidos/${partidoId}/anular`),
  // Cuadro. `siembra` solo para categorías de solo eliminación (el orden que salió del sorteo).
  armarCuadro: (categoriaId: number, siembra?: number[]) =>
    postear<{ ok: true }>(`/admin/categorias/${categoriaId}/cuadro`, siembra ? { siembra } : {}),
  deshacerCuadro: (categoriaId: number) => postear<{ ok: true }>(`/admin/categorias/${categoriaId}/deshacer-cuadro`),
  // Premios: un texto por puesto; '' = sin premio.
  guardarPremios: (categoriaId: number, premios: Record<number, string>) =>
    postear<{ ok: true }>(`/admin/categorias/${categoriaId}/premios`, { premios }),

  // Programación
  ponerHorario: (partidoId: number, h: Horario) => postear<{ ok: true }>(`/admin/partidos/${partidoId}/horario`, h),
  guardarHorarios: (torneoId: number, horarios: ({ id: number } & Horario)[], accion: 'manual' | 'asistente' = 'manual') =>
    postear<{ guardados: number }>(`/admin/torneos/${torneoId}/horarios`, { horarios, accion }),
  iniciarEnCancha: (partidoId: number, cancha: number) => postear<{ok: true}>(`/admin/partidos/${partidoId}/iniciar`, {en_juego: true, cancha}),
  enJuego: (partidoId: number, en_juego: boolean) => postear<{ ok: true }>(`/admin/partidos/${partidoId}/en-juego`, { en_juego }),
  sortearDesempate: (grupoId: number) => postear<{ orden: number[] }>(`/admin/grupos/${grupoId}/sorteo`),
};

/** El mensaje de un error para mostrar, venga de donde venga. */
export function mensajeDe(e: unknown): string {
  return e instanceof Error ? e.message : 'Algo falló.';
}
